import type { Ref } from "vue";
import { markMetaPixelReady, useCookieConsent } from "~/composables/meta-pixel";
import { useProfileStore } from "~/stores/get-profile-data";
import {
  FBCLID_COOKIE,
  fbcFromStoredClick,
  normalizeMetaUserData,
  type MetaUserData,
} from "~/shared/meta-user-data";

const NINETY_DAYS = 60 * 60 * 24 * 90;

// Loads the Meta Pixel after cookie consent and tracks a PageView on every route change.
export default defineNuxtPlugin((nuxtApp) => {
  const { metaPixelId } = useRuntimeConfig().public;
  const route = useRoute();
  const router = useRouter();

  // Embedded registration forms are covered by the parent site's tracking and consent.
  if (!metaPixelId || route.query.isIframe === "true") return;

  const { consent } = useCookieConsent();
  const locale = (nuxtApp.$i18n as any).locale as Ref<string>;
  const authenticated = useCookie("authenticated");
  const customerIdCookie = useCookie("customer_id");
  const profileStore = useProfileStore();
  const storedClick = useCookie<string | null>(FBCLID_COOKIE, {
    maxAge: NINETY_DAYS,
    sameSite: "lax",
    encode: (value) => value ?? "",
    decode: (value) => value || null,
  });
  const fbc = useCookie<string | null>("_fbc", {
    maxAge: NINETY_DAYS,
    sameSite: "lax",
    encode: (value) => value ?? "",
    decode: (value) => value || null,
  });

  let loadStarted = false;
  let loaded = false;
  let lastTrackedPath = "";

  // Remember the ad click from the landing URL (regardless of consent) so it still counts
  // when the user accepts cookies on a later page or visit. Only sent to Meta after consent.
  function rememberAdClick(fbclid: unknown) {
    if (typeof fbclid === "string" && fbclid && !storedClick.value?.endsWith(`.${fbclid}`)) {
      storedClick.value = `${Date.now()}.${fbclid}`;
    }
  }

  function trackPageView(path: string) {
    if (path === lastTrackedPath) return;
    lastTrackedPath = path;
    window.fbq?.("track", "PageView");
  }

  async function sha256(value: string) {
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
  }

  // Advanced matching: logged-in users' details let Meta match browser-only events.
  async function getUserData(): Promise<MetaUserData> {
    if (!authenticated.value) return {};

    try {
      const customerId = customerIdCookie.value;
      const customer =
        profileStore.profile && !Array.isArray(profileStore.profile)
          ? profileStore.profile
          : (
              await $fetch<any>("/api/auth", {
                method: "POST",
                body: { type: "getProfile", payload: customerId, locale: locale.value },
              })
            )?.data?.customer;

      const userData = normalizeMetaUserData(
        { ...customer, customer_id: customerId ?? customer?.id },
        locale.value
      );

      // The Pixel doesn't hash external_id; hash it so it matches the Conversions API value.
      if (userData.external_id) userData.external_id = await sha256(userData.external_id);

      return userData;
    } catch {
      return {};
    }
  }

  async function load() {
    if (loaded) {
      window.fbq?.("consent", "grant");
      return;
    }
    if (loadStarted) return;
    loadStarted = true;

    // Restore the ad click before the Pixel starts, so it reads _fbc instead of losing it.
    const fbclid = storedClick.value?.slice(storedClick.value.indexOf(".") + 1);
    if (fbclid && !fbc.value?.endsWith(`.${fbclid}`)) {
      fbc.value = fbcFromStoredClick(storedClick.value) ?? null;
    }

    const userData = await getUserData();

    /* Meta Pixel base code */
    (function (f: any, b: Document, e: string, v: string) {
      if (f.fbq) return;
      const n: any = (f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      });
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = "2.0";
      n.queue = [];
      const t = b.createElement(e) as HTMLScriptElement;
      t.async = true;
      t.src = v;
      const s = b.getElementsByTagName(e)[0];
      s.parentNode?.insertBefore(t, s);
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");

    window.fbq?.("init", String(metaPixelId), userData);
    loaded = true;

    // Consent may have been withdrawn while the profile was loading.
    if (consent.value !== "granted") window.fbq?.("consent", "revoke");

    trackPageView(route.fullPath);
    markMetaPixelReady();
  }

  rememberAdClick(route.query.fbclid);

  if (consent.value === "granted") load();

  watch(consent, (value) => {
    if (value === "granted") {
      load();
    } else if (loaded) {
      window.fbq?.("consent", "revoke");
    }
  });

  router.afterEach((to) => {
    rememberAdClick(to.query.fbclid);
    if (loaded && consent.value === "granted") trackPageView(to.fullPath);
  });
});
